<template>
  <div class="crud-container" data-aos="fade-right">
    <CrudToolbar
      title="Tipos de evento"
      v-model="filtro"
      searchPlaceholder="Buscar por nombre o clave..."
    >
      <template #actions>
        <button class="btn btn-nuevo btn-nuevo-header" @click="abrirModal(null)">
          <i class="bi bi-tag-fill"></i>
          <span class="btn-nuevo-texto ms-2">Nueva categoría</span>
        </button>
      </template>
    </CrudToolbar>

    <div class="divisan-table-container shadow-lg d-none d-lg-block">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>

      <table v-else class="table table-divisan align-middle mb-0">
        <thead>
          <tr>
            <th class="ps-4">
              <ColumnFilterMenu
                field="nombre"
                label="Nombre de categoría"
                :filterable="false"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th>
              <ColumnFilterMenu
                field="nombre_clave"
                label="Clave (Código)"
                :filterable="false"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">
              <ColumnFilterMenu
                field="estado_label"
                label="Estado"
                :items="tiposParaFiltro"
                :sortField="ordenCampo"
                :sortAsc="ordenAsc"
                v-model="filtroEstadoSet"
                @update:sort="onSort"
              />
            </th>
            <th class="text-center">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tipo in tiposPaginados" :key="tipo.id">
            <td class="ps-4">
              <span class="fw-bold text-cafe">{{ tipo.nombre }}</span>
            </td>
            <td>
              <span class="badge-clave">{{ tipo.nombre_clave }}</span>
            </td>
            <td class="text-center">
              <span :class="['badge-estado', tipo.activo ? 'activo' : 'inactivo']">
                {{ tipo.activo ? "Activo" : "Inactivo" }}
              </span>
            </td>
            <td class="text-center">
              <div class="d-flex justify-content-center gap-2">
                <button class="btn-circular" @click="abrirModal(tipo)" title="Editar">
                  <i class="bi bi-pencil-square"></i>
                </button>
                <button
                  class="btn-circular text-danger"
                  @click="confirmarBorrado(tipo)"
                  title="Desactivar"
                >
                  <i class="bi bi-trash3-fill"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="tiposPaginados.length === 0">
            <td colspan="4" class="text-center py-5 text-cafe opacity-50 fw-bold">
              No se encontraron resultados
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-if="totalPaginas > 1"
        class="pagination-wrapper d-flex justify-content-between align-items-center px-4 py-3"
      >
        <small class="text-cafe fw-bold"
          >Página {{ paginaActual }} de {{ totalPaginas }}</small
        >
        <div class="d-flex gap-2">
          <button
            class="btn-pag"
            :disabled="paginaActual === 1"
            @click="cambiarPagina(paginaActual - 1)"
          >
            <i class="bi bi-chevron-left"></i>
          </button>
          <button
            class="btn-pag"
            :disabled="paginaActual === totalPaginas"
            @click="cambiarPagina(paginaActual + 1)"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="d-lg-none">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-verde"></div>
      </div>
      <div
        v-else
        v-for="tipo in tiposPaginados"
        :key="'mob-' + tipo.id"
        class="divisan-mobile-card mb-2 px-3 py-2 shadow-sm"
      >
        <!-- Fila 1: nombre + botones -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="fw-bold text-cafe card-name">{{ tipo.nombre }}</span>
          <div class="d-flex gap-2">
            <button class="btn-circular-sm" @click="abrirModal(tipo)" title="Editar">
              <i class="bi bi-pencil-square text-verde"></i>
            </button>
            <button
              class="btn-circular-sm"
              @click="confirmarBorrado(tipo)"
              title="Desactivar"
            >
              <i class="bi bi-trash3-fill text-danger"></i>
            </button>
          </div>
        </div>
        <!-- Fila 2: clave + estado -->
        <div class="d-flex align-items-center gap-2">
          <span class="badge-clave">{{ tipo.nombre_clave }}</span>
          <span :class="['badge-estado', tipo.activo ? 'activo' : 'inactivo']">
            {{ tipo.activo ? "Activo" : "Inactivo" }}
          </span>
        </div>
      </div>

      <!-- Paginación móvil -->
      <div
        v-if="totalPaginas > 1"
        class="pagination-mobile d-flex justify-content-between align-items-center px-1 py-2 mt-1"
      >
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === 1"
          @click="cambiarPagina(paginaActual - 1)"
        >
          <i class="bi bi-chevron-left"></i> Anterior
        </button>
        <small class="text-cafe fw-bold">{{ paginaActual }} / {{ totalPaginas }}</small>
        <button
          class="btn-pag-mobile"
          :disabled="paginaActual === totalPaginas"
          @click="cambiarPagina(paginaActual + 1)"
        >
          Siguiente <i class="bi bi-chevron-right"></i>
        </button>
      </div>
    </div>

    <div
      class="modal fade"
      id="modalTipo"
      tabindex="-1"
      aria-hidden="true"
      ref="modalElement"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content divisan-modal border-0">
          <div class="modal-header border-0 pb-0">
            <h5 class="modal-title fw-bold text-cafe">
              {{ editando ? "Editar categoría" : "Nueva categoría" }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <form @submit.prevent="guardarTipo">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label label-divisan">Nombre público</label>
                <input
                  v-model="form.nombre"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. Preventivo"
                />
              </div>
              <div class="mb-3">
                <label class="form-label label-divisan"
                  >Nombre clave (Uso de sistema)</label
                >
                <input
                  v-model="form.nombre_clave"
                  type="text"
                  class="form-control divisan-input-modal"
                  required
                  placeholder="Ej. PREV"
                />
                <small class="text-muted"
                  >Se recomienda usar mayúsculas (máx. 10 carac.)</small
                >
              </div>
            </div>
            <div class="modal-footer border-0 pt-0">
              <button
                type="submit"
                class="btn btn-nuevo w-100 py-2"
                :disabled="guardando"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                ></span>
                {{ editando ? "Actualizar cambios" : "Guardar categoría" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import {
  getTiposEvento,
  crearTipoEvento,
  actualizarTipoEvento,
  eliminarTipoEvento,
} from "@/utils/api_backend";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import CrudToolbar from "@/components/Shared/CrudToolbar.vue";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

// ── Estado ─────────────────────────────────────────────────
const tipos = ref([]);
const filtro = ref("");
const filtroEstadoSet = ref(new Set());
const loading = ref(false);
const guardando = ref(false);
const editando = ref(false);
const modalElement = ref(null);
const form = ref({ id: null, nombre: "", nombre_clave: "" });
const paginaActual = ref(1);
const porPagina = ref(5);
const ordenCampo = ref("nombre");
const ordenAsc = ref(true);
let modalInstance = null;

function onSort({ field, asc }) {
  ordenCampo.value = field;
  ordenAsc.value = asc;
}

// Resetear página al filtrar
watch([filtro, filtroEstadoSet], () => {
  paginaActual.value = 1;
});

// ── Computed ───────────────────────────────────────────────
const tiposPorTexto = computed(() => {
  const s = filtro.value.toLowerCase();
  if (!s) return tipos.value;
  return tipos.value.filter(
    (t) => t.nombre.toLowerCase().includes(s) || t.nombre_clave.toLowerCase().includes(s)
  );
});

const tiposParaFiltro = computed(() =>
  tiposPorTexto.value.map((t) => ({
    ...t,
    estado_label: t.activo ? "Activo" : "Inactivo",
  }))
);

const tiposFiltrados = computed(() =>
  tiposPorTexto.value.filter((t) => {
    return (
      !filtroEstadoSet.value.size ||
      filtroEstadoSet.value.has(t.activo ? "Activo" : "Inactivo")
    );
  })
);

const tiposOrdenados = computed(() => {
  return [...tiposFiltrados.value].sort((a, b) => {
    let valA = a[ordenCampo.value];
    let valB = b[ordenCampo.value];
    if (typeof valA === "string") {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    if (valA < valB) return ordenAsc.value ? -1 : 1;
    if (valA > valB) return ordenAsc.value ? 1 : -1;
    return 0;
  });
});

const totalPaginas = computed(() =>
  Math.ceil(tiposOrdenados.value.length / porPagina.value)
);

const tiposPaginados = computed(() => {
  const inicio = (paginaActual.value - 1) * porPagina.value;
  return tiposOrdenados.value.slice(inicio, inicio + porPagina.value);
});

function cambiarPagina(pagina) {
  if (pagina >= 1 && pagina <= totalPaginas.value) paginaActual.value = pagina;
}

// ── Métodos ────────────────────────────────────────────────
async function cargarTipos() {
  loading.value = true;
  try {
    tipos.value = await getTiposEvento();
  } catch {
    Swal.fire("Error", "No se pudieron obtener las categorías", "error");
  } finally {
    loading.value = false;
  }
}

function abrirModal(tipo = null) {
  if (tipo) {
    editando.value = true;
    form.value = { ...tipo };
  } else {
    editando.value = false;
    form.value = { id: null, nombre: "", nombre_clave: "" };
  }
  modalInstance.show();
}

async function guardarTipo() {
  guardando.value = true;
  try {
    if (editando.value) {
      await actualizarTipoEvento(form.value.id, form.value);
    } else {
      await crearTipoEvento(form.value);
    }
    await cargarTipos();
    modalInstance.hide();
    Swal.fire({
      icon: "success",
      title: "Operación exitosa",
      text: `Categoría ${editando.value ? "actualizada" : "creada"} correctamente`,
      timer: 2000,
      showConfirmButton: false,
    });
  } catch {
    Swal.fire("Error", "No se pudo guardar la categoría", "error");
  } finally {
    guardando.value = false;
  }
}

async function confirmarBorrado(tipo) {
  const res = await Swal.fire({
    title: `Eliminar ${tipo.nombre}?`,
    text: "Confirmar para eliminar esta categoría.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "var(--d-brown)",
    cancelButtonColor: "var(--d-green)",
  });
  if (res.isConfirmed) {
    try {
      await eliminarTipoEvento(tipo.id);
      await cargarTipos();
      Swal.fire("Listo", "Categoría deshabilitada", "success");
    } catch {
      Swal.fire("Error", "No se pudo completar la desactivación", "error");
    }
  }
}

onMounted(() => {
  cargarTipos();
  modalInstance = new Modal(modalElement.value);
});
</script>

<style scoped>
/* ESTILOS BASE DIVISAN */
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

/* BOTONES */
.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
}
.btn-nuevo:hover {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}

/* CARDS */
.divisan-card {
  background-color: var(--d-cream);
  border-radius: 12px;
  border-left: 5px solid var(--d-green);
}
.divisan-input {
  background-color: white !important;
  border: 1px solid var(--d-green);
  color: var(--d-brown) !important;
  border-radius: 8px;
}
.search-wrapper i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  z-index: 5;
}
.search-wrapper {
  position: relative;
}
.search-wrapper input {
  padding-left: 35px;
}

/* TABLA */
.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  box-shadow: var(--d-elevation-2);
  overflow: hidden;
}
.table-divisan thead {
  background-color: var(--d-cream);
  color: var(--d-brown);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  border-bottom: 1px solid var(--d-border);
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 15px;
}
.table-divisan tbody tr {
  transition: background-color 0.15s ease;
}
.table-divisan tbody tr:hover {
  background-color: var(--d-nav-hover-bg);
}

/* BADGES */
.badge-clave {
  background: rgba(96, 108, 56, 0.1);
  color: var(--d-green-dark);
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--d-font-mono);
  font-size: 0.78rem;
  border: 1px solid rgba(96, 108, 56, 0.2);
}

.card-name {
  font-size: 0.92rem;
}
.badge-estado {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
}
.activo {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.inactivo {
  background-color: var(--d-brown);
  color: var(--d-cream);
}

/* MÓVIL */
.divisan-mobile-card {
  background-color: var(--d-cream);
  border-radius: var(--d-radius-lg);
  border-left: 6px solid var(--d-green);
  box-shadow: var(--d-elevation-1);
  transition: box-shadow 0.15s ease;
}
.divisan-mobile-card:active {
  box-shadow: var(--d-elevation-2);
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
}

/* MODAL */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
}

.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 1px solid var(--d-border);
}

.pagination-wrapper {
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background-color: rgba(96, 108, 56, 0.05);
}
.btn-pag {
  background: white;
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 8px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-pag:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag:disabled {
  opacity: 0.35;
  cursor: default;
}
.btn-nuevo-header {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.btn-circular-sm {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
  font-size: 0.8rem;
}
.btn-circular-sm:hover {
  background-color: rgba(96, 108, 56, 0.1);
}
.pagination-mobile {
  border-top: 1px solid rgba(64, 34, 8, 0.15);
}
.btn-pag-mobile {
  background: var(--d-cream);
  border: 1px solid var(--d-green);
  color: var(--d-brown);
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 110px;
  justify-content: center;
}
.btn-pag-mobile:hover:not(:disabled) {
  background-color: var(--d-green);
  color: var(--d-cream);
}
.btn-pag-mobile:disabled {
  opacity: 0.35;
  cursor: default;
}
@media (max-width: 991.98px) {
  .btn-nuevo-texto {
    display: none;
  }
  .btn-nuevo-header {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
